import { observer } from "mobx-react";
import React, { useContext } from "react";
import { Textarea, Button, Group, Paper, Stack, Alert, Anchor, Text } from "@mantine/core";
import { IconAlertCircle, IconSearch, IconTrash } from "@tabler/icons-react";
import { DecodeInvoiceStoreContext } from "../stores/DecodeInvoiceStore";

/** When the paste isn't a BOLT11 invoice but something another Rizful tool handles. */
const otherToolHint = (raw: string): { text: string; href: string; label: string } | null => {
  const input = raw.trim().replace(/^(lightning|bitcoin):/i, "");
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input)) {
    return {
      text: "That looks like a Lightning Address, not an invoice.",
      href: `/lightning-address?address=${encodeURIComponent(input)}`,
      label: "Test it with the Lightning Address tester",
    };
  }
  if (/^lnurl1/i.test(input)) {
    return {
      text: "That's an LNURL, not an invoice: it's a link a wallet opens to ask for an invoice.",
      href: "/lightning-address",
      label: "Lightning Address & LNURL tester",
    };
  }
  if (/^(bc1|tb1|[13])[a-z0-9]{20,}$/i.test(input) && !/^ln/i.test(input)) {
    return {
      text: "That looks like a Bitcoin address, not a Lightning invoice.",
      href: `/check_bitcoin_address?address=${encodeURIComponent(input)}`,
      label: "Check it with the Bitcoin address checker",
    };
  }
  if (/^[0-9a-f]{64}$/i.test(input)) {
    return {
      text: "That looks like a Bitcoin transaction ID (or a payment hash).",
      href: `/unconfirmed-bitcoin-transaction?txid=${input}`,
      label: "Check the transaction",
    };
  }
  return null;
};

const InvoiceInput: React.FC = observer(() => {
  const store = useContext(DecodeInvoiceStoreContext);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      store.decodeInvoice();
    }
  };

  return (
    <Paper
      p="md"
      radius="md"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.4)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
      }}
    >
      <Stack gap="sm">
        <Textarea
          placeholder="Paste a BOLT11 invoice here (lnbc1...)"
          value={store.invoiceInput}
          onChange={(e) => store.setInvoiceInput(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          minRows={4}
          maxRows={8}
          autosize
          styles={{
            input: {
              fontFamily: "monospace",
              fontSize: "12px",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              color: "white",
              "&::placeholder": {
                color: "rgba(255, 255, 255, 0.4)",
              },
            },
          }}
        />

        {store.decodeError && store.hasAttemptedDecode && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {store.decodeError}
            {(() => {
              const hint = otherToolHint(store.invoiceInput);
              return hint ? (
                <Text size="sm" mt={6} c="gray.3">
                  {hint.text}{" "}
                  <Anchor href={hint.href} c="orange.4">
                    {hint.label} →
                  </Anchor>
                </Text>
              ) : null;
            })()}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconTrash size={16} />}
            onClick={() => store.clear()}
            disabled={!store.invoiceInput || store.isDecoding}
          >
            Clear
          </Button>
          <Button
            leftSection={<IconSearch size={16} />}
            onClick={() => store.decodeInvoice()}
            disabled={!store.invoiceInput.trim() || store.isDecoding}
            loading={store.isDecoding}
          >
            Decode Invoice
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
});

export default InvoiceInput;
